import { Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { Header } from '@/app/components/Header';
import { Redacted } from '@/app/components/Redacted';
import { SeoManager } from '@/app/components/SeoManager';
import { LangProvider } from '@/app/context/LangContext';
import { useLang } from '@/app/hooks/useLang';
import {
  BandDossierPage,
  ContactPage,
  DossiersPage,
  Home,
  LoginPage,
  MerchPage,
  ReleaseDetailPage,
  ReleasesPage,
  RosterPage,
} from '@/app/routes';

export function AppShell() {
  const { t } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const showBack = location.pathname !== '/' && location.key !== 'default';

  const handleBackToTop = () => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  return (
    <>
      <SeoManager />
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <div className="min-h-screen bg-transparent flex flex-col">
        <Header />
        <div className="flex-1">
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/roster" element={<RosterPage />} />
              <Route path="/roster/:id" element={<BandDossierPage />} />
              <Route path="/releases" element={<ReleasesPage />} />
              <Route path="/releases/:id" element={<ReleaseDetailPage />} />
              <Route path="/merch" element={<MerchPage />} />
              <Route path="/dossiers" element={<DossiersPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/login" element={<LoginPage />} />
            </Routes>
          </Suspense>
        </div>
        <div className="mx-auto mb-1 flex flex-wrap items-center justify-center gap-3 px-4">
          {showBack && (
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="tab-control"
              aria-label={t('action.previousPage')}
            >
              {t('action.previousPage')}
            </button>
          )}
          <button
            type="button"
            onClick={handleBackToTop}
            className="tab-control"
            aria-label="Back to top"
          >
            ↑ Top
          </button>
        </div>
        <footer className="dim border-t py-6 mt-2">
          <div className="max-w-6xl mx-auto space-y-2 px-4 text-center">
            <p>{t('footer.disclaimer')}</p>
            <p>
              v3.0.0 //{' '}
              <a href="https://ironsignalworks.com" target="_blank" rel="noopener noreferrer">
                {t('footer.deployedBy')}
              </a>
              {' // DOC-NSR // '}
              <Redacted text="RETAIN INDEFINITELY" />
              {' // 永久保管'}
            </p>
          </div>
        </footer>
      </div>
    </>
  );
}

export default function App() {
  return (
    <LangProvider>
      <BrowserRouter basename={import.meta.env.BASE_URL}>
        <AppShell />
      </BrowserRouter>
    </LangProvider>
  );
}
